// src/pages/TripDetail.js

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Navigation, Clock, Route, Car, Copy, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { trips as tripsApi, reasons as reasonsApi, vehicles as vehiclesApi } from '../services/api';

// Brand
const PRIMARY    = '#00ACC1';
const BIZ_COLOR  = '#FF9800';
const PRIV_COLOR = '#4CAF50';

// Helpers
const fmtKm = (km) => km ? parseFloat(km).toFixed(2) + ' km' : '—';

const fmtDate = (str) => str
  ? new Date(str).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  : '';

const fmtTime = (str) => str
  ? new Date(str).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
  : '';

const fmtDuration = (startStr, endStr) => {
  if (!startStr || !endStr) return '—';
  const diff = Math.floor((new Date(endStr) - new Date(startStr)) / 1000);
  if (diff <= 0) return '—';
  const h = Math.floor(diff / 3600), m = Math.floor((diff % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return '< 1m';
};

const fmtCoord = (lat, lng) => { 
  if (!lat || !lng) return null;
  const la = parseFloat(lat), ln = parseFloat(lng);
  if (isNaN(la) || isNaN(ln)) return null;
  return `${Math.abs(la).toFixed(6)}${la < 0 ? ' S' : ' N'}, ${Math.abs(ln).toFixed(6)}${ln < 0 ? ' W' : ' E'}`;
};

const mapsLink = (lat, lng) =>
  lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : null;

// Card
const Card = ({ children, className = '', theme }) => (
  <div className={`rounded-2xl border ${className}`}
    style={{
      backgroundColor: theme.card,
      borderColor:     theme.border,
      boxShadow:       theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.06)',
    }}>
    {children}
  </div>
);

// Section label
const SectionLabel = ({ children, theme }) => (
  <p className="text-[10px] font-bold uppercase tracking-wider mb-3"
    style={{ color: theme.textMuted }}>
    {children}
  </p>
);

// Leaflet map
// Priority for start pin: zone coords > previous trip end > GPS
// Priority for end pin: zone coords > GPS
const TripMap = ({ trip, theme }) => {
  const mapRef      = { current: null };
  const mapInstance = { current: null };

  // resolve best start coords: zone > prev trip end > raw GPS
  const rawStartLat = parseFloat(trip.start_zone_lat) || parseFloat(trip.prev_end_lat) || parseFloat(trip.start_lat);
  const rawStartLng = parseFloat(trip.start_zone_lng) || parseFloat(trip.prev_end_lng) || parseFloat(trip.start_lng);
  const startLat = rawStartLat;
  const startLng = rawStartLng;

  // resolve best end coords: zone > raw GPS
  const endLat = parseFloat(trip.end_zone_lat) || parseFloat(trip.end_lat);
  const endLng = parseFloat(trip.end_zone_lng) || parseFloat(trip.end_lng);

  const hasStart = !isNaN(startLat) && !isNaN(startLng);
  const hasEnd   = !isNaN(endLat)   && !isNaN(endLng);

  useEffect(() => {
    if (!hasStart && !hasEnd) return;

    const initMap = () => {
      const el = document.getElementById('trip-map');
      if (!el || mapInstance.current) return;
      const L   = window.L;
        const map = L.map(el, { zoomControl: false });
      mapInstance.current = map;

      // CARTO raster tiles require an API key, attribution must stay visible
      const style = theme.isDark ? 'dark_all' : 'light_all';
      L.tileLayer(
        `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_KEY}`,
        { maxZoom: 19, attribution: '&copy; OpenStreetMap contributors &copy; CARTO' }
      ).addTo(map);

      const startIcon = L.divIcon({
        className: '',
        html: '<div style="width:14px;height:14px;background:#4CAF50;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>',
        iconAnchor: [7, 7],
      });
      const endIcon = L.divIcon({
        className: '',
        html: '<div style="width:14px;height:14px;background:#ef4444;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>',
        iconAnchor: [7, 7],
      });

      const bounds = [];

      if (hasStart) {
        L.marker([startLat, startLng], { icon: startIcon }).addTo(map)
          .bindPopup(trip.start_zone_name || trip.start_address || 'Start');
        bounds.push([startLat, startLng]);
      }
      if (hasEnd) {
        L.marker([endLat, endLng], { icon: endIcon }).addTo(map)
          .bindPopup(trip.end_zone_name || trip.end_address || 'End');
        bounds.push([endLat, endLng]);
      }

      if (bounds.length > 1) {
        map.fitBounds(bounds, { padding: [48, 48] });
      } else {
        map.setView(bounds[0], 15);
      }
    };

    if (window.L) {
      setTimeout(initMap, 0);
    } else {
      const link  = document.createElement('link');
      link.rel    = 'stylesheet';
      link.href   = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      const script  = document.createElement('script');
      script.src    = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => setTimeout(initMap, 0);
      document.head.appendChild(script);
    }

    return () => {
      if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; }
    };
  }, [trip.id, theme.isDark]);

  if (!hasStart && !hasEnd) {
    return (
      <Card theme={theme} className="flex items-center justify-center" style={{ height: 160 }}>
        <div className="text-center">
          <MapPin size={24} className="mx-auto mb-2" style={{ color: theme.textMuted }} />
          <p className="text-xs" style={{ color: theme.textMuted }}>No GPS coordinates</p>
        </div>
      </Card>
    );
  }

  return (
    <div id="trip-map" className="rounded-2xl overflow-hidden"
      style={{ height: 220, boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.06)' }} />
  );
};

// Main
export default function TripDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const theme    = useTheme();

  const [trip,        setTrip]        = useState(null);
  const [reasonList,  setReasonList]  = useState([]);
  const [vehicleList, setVehicleList] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [error,       setError]       = useState('');
  const [copied,      setCopied]      = useState('');

  // Editable state
  const [category,     setCategory]     = useState(null);
  const [reasonId,     setReasonId]     = useState(null);
  const [reasonDetail, setReasonDetail] = useState('');
  const [notes,        setNotes]        = useState('');
  const [vehicleId,    setVehicleId]    = useState(null);

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      const [tripRes, reasonRes, vehicleRes] = await Promise.all([
        tripsApi.get(id),
        reasonsApi.list(),
        vehiclesApi.list(),
      ]);
      if (tripRes.success && tripRes.data) {
        const t = tripRes.data;
        setTrip(t);
        setCategory(t.category);
        setReasonId(t.reason_id);
        setReasonDetail(t.reason_detail || '');
        setNotes(t.notes || '');
        setVehicleId(t.vehicle_id);
      } else {
        setError(tripRes.error || 'Trip not found');
      }
      if (reasonRes.success && reasonRes.data) {
        // system defaults first, then custom alphabetical
        setReasonList([...reasonRes.data].sort((a, b) => {
          const ad = Number(a.is_default) || Number(a.is_system);
          const bd = Number(b.is_default) || Number(b.is_system);
          if (ad && !bd) return -1;
          if (!ad && bd) return 1;
          return a.name.localeCompare(b.name);
        }));
      }
      if (vehicleRes.success && vehicleRes.data) setVehicleList(vehicleRes.data);
      setLoading(false);
    }
    loadAll();
  }, [id]);

  async function handleSave() {
    setSaving(true);
    // validate reason matches category
    const selectedReason = reasonList.find(r => r.id === reasonId);
    const cleanReasonId  = selectedReason?.category === category ? reasonId : null;

    const res = await tripsApi.update(id, {
      category,
      reason_id:     cleanReasonId,
      reason_detail: reasonDetail.trim() || null,
      notes:         notes.trim() || null,
      vehicle_id:    vehicleId,
    });
    setSaving(false);
    if (res.success && res.data) {
      setTrip(res.data);
      setReasonId(cleanReasonId);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } else {
      setError(res.error || 'Could not save changes');
    }
  }

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  // Reasons filtered to current category, selected first
  const filteredReasons = reasonList
    .filter(r => !category || r.category === category)
    .sort((a, b) => {
      if (a.id === reasonId && b.id !== reasonId) return -1;
      if (b.id === reasonId && a.id !== reasonId) return 1;
      return 0;
    });

  const accentColor = category === 'business' ? BIZ_COLOR : category === 'private' ? PRIV_COLOR : theme.textMuted;

  if (loading) return (
    <div className="flex items-center justify-center min-h-[80vh]">
      <div className="w-8 h-8 border-2 rounded-full animate-spin"
        style={{ borderColor: theme.border, borderTopColor: PRIMARY }} />
    </div>
  );

  if (error && !trip) return (
    <div className="px-4 md:px-8 py-8 min-h-screen" style={{ backgroundColor: theme.background }}>
      <div className="px-4 py-3 rounded-2xl text-sm"
        style={{ backgroundColor: theme.isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2', color: '#f87171' }}>
        {error}
      </div>
    </div>
  );

  if (!trip) return null;

  const startCoord = fmtCoord(trip.start_lat, trip.start_lng);
  const endCoord   = fmtCoord(trip.end_lat,   trip.end_lng);

  return (
    <div className="px-3 sm:px-4 md:px-8 py-4 sm:py-6 md:py-8 min-h-screen"
      style={{ backgroundColor: theme.background }}>

      {/* Back */}
      <button onClick={() => navigate('/portal/trips')}
        className="flex items-center gap-2 text-xs font-medium mb-4 transition-opacity hover:opacity-70 p-1 -ml-1"
        style={{ color: theme.textSecondary }}>
        <ArrowLeft size={14} /> Back to Trips
      </button>

      <div className="max-w-2xl mx-auto space-y-3">

        {/* Map */}
        <TripMap trip={trip} theme={theme} />

        {/* Date + stats */}
        <Card theme={theme} className="p-4 sm:p-5">
          <p className="text-base sm:text-lg font-bold mb-1" style={{ color: theme.text }}>
            {fmtDate(trip.start_time)}
          </p>
          <p className="text-sm mb-4" style={{ color: theme.textMuted }}>
            {fmtTime(trip.start_time)}{trip.end_time ? ` — ${fmtTime(trip.end_time)}` : ''}
          </p>
          <div className="flex items-center">
            {[
              { icon: Navigation, label: 'Distance', value: fmtKm(trip.distance_km) },
              { icon: Clock,      label: 'Duration', value: fmtDuration(trip.start_time, trip.end_time) },
              { icon: Route,      label: 'Pings',    value: trip.ping_count ?? 0 },
            ].map((s, i, arr) => (
              <div key={s.label} className="flex-1 flex flex-col items-center gap-1">
                <s.icon size={14} style={{ color: PRIMARY }} />
                <p className="text-[10px]" style={{ color: theme.textMuted }}>{s.label}</p>
                <p className="text-sm font-bold" style={{ color: theme.text }}>{s.value}</p>
                {i < arr.length - 1 && (
                  <div className="absolute" />
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Route */}
        {(trip.start_address || trip.start_zone_name || trip.end_address || trip.end_zone_name) && (
          <Card theme={theme} className="p-4 sm:p-5">
            <SectionLabel theme={theme}>Route</SectionLabel>
            {(trip.start_address || trip.start_zone_name) && (
              <div className="flex items-start gap-3 mb-4">
                <div className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ backgroundColor: '#4CAF50' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] mb-0.5" style={{ color: theme.textMuted }}>Start</p>
                  {trip.start_zone_name && (
                    <p className="text-sm font-semibold mb-0.5" style={{ color: PRIMARY }}>{trip.start_zone_name}</p>
                  )}
                  {trip.start_address && (
                    <p className="text-sm" style={{ color: theme.text }}>{trip.start_address}</p>
                  )}
                  {startCoord && (
                    <p className="text-[10px] font-mono mt-0.5" style={{ color: theme.textMuted }}>{startCoord}</p>
                  )}
                </div>
                {trip.start_lat && trip.start_lng && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => copyToClipboard(`${trip.start_lat},${trip.start_lng}`, 'start')}
                      className="p-1.5 rounded-lg transition-opacity hover:opacity-70">
                      {copied === 'start' ? <Check size={12} style={{ color: '#22c55e' }} /> : <Copy size={12} style={{ color: theme.textMuted }} />}
                    </button>
                    <a href={mapsLink(trip.start_lat, trip.start_lng)} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 rounded-lg transition-opacity hover:opacity-70">
                      <MapPin size={12} style={{ color: PRIMARY }} />
                    </a>
                  </div>
                )}
              </div>
            )}
            {(trip.end_address || trip.end_zone_name) && (
              <div className="flex items-start gap-3">
                <div className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ backgroundColor: '#ef4444' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] mb-0.5" style={{ color: theme.textMuted }}>End</p>
                  {trip.end_zone_name && (
                    <p className="text-sm font-semibold mb-0.5" style={{ color: PRIMARY }}>{trip.end_zone_name}</p>
                  )}
                  {trip.end_address && (
                    <p className="text-sm" style={{ color: theme.text }}>{trip.end_address}</p>
                  )}
                  {endCoord && (
                    <p className="text-[10px] font-mono mt-0.5" style={{ color: theme.textMuted }}>{endCoord}</p>
                  )}
                </div>
                {trip.end_lat && trip.end_lng && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => copyToClipboard(`${trip.end_lat},${trip.end_lng}`, 'end')}
                      className="p-1.5 rounded-lg transition-opacity hover:opacity-70">
                      {copied === 'end' ? <Check size={12} style={{ color: '#22c55e' }} /> : <Copy size={12} style={{ color: theme.textMuted }} />}
                    </button>
                    <a href={mapsLink(trip.end_lat, trip.end_lng)} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 rounded-lg transition-opacity hover:opacity-70">
                      <MapPin size={12} style={{ color: PRIMARY }} />
                    </a>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Classification */}
        <Card theme={theme} className="p-4 sm:p-5">
          <SectionLabel theme={theme}>Classification</SectionLabel>

          {/* Category toggle */}
          <div className="grid grid-cols-2 gap-2.5 mb-4">
            {['business', 'private'].map(cat => {
              const active = category === cat;
              const color  = cat === 'business' ? BIZ_COLOR : PRIV_COLOR;
              return (
                <button key={cat} onClick={() => { setCategory(cat); setReasonId(null); }}
                  className="h-11 rounded-xl border-2 font-semibold text-sm transition-all active:scale-95"
                  style={{
                    borderColor:     active ? color : theme.border,
                    backgroundColor: active ? `${color}18` : 'transparent',
                    color:           active ? color : theme.textMuted,
                  }}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              );
            })}
          </div>

          {/* Reason chips */}
          {category && filteredReasons.length > 0 && (
            <>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: theme.textMuted }}>
                Reason
              </p>
              <div className="flex gap-2 overflow-x-auto pb-2 mb-1 scrollbar-hide">
                {filteredReasons.map(r => {
                  const active = reasonId === r.id;
                  return (
                    <button key={r.id} onClick={() => setReasonId(active ? null : r.id)}
                      className="shrink-0 px-3.5 py-2 rounded-full border text-xs font-semibold transition-all active:scale-95"
                      style={{
                        borderColor:     active ? PRIMARY : theme.border,
                        backgroundColor: active ? `${PRIMARY}18` : 'transparent',
                        color:           active ? PRIMARY : theme.textMuted,
                      }}>
                      {r.name}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Detail */}
          {category && (
            <>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-2 mt-3" style={{ color: theme.textMuted }}>
                Detail
              </p>
              <input
                value={reasonDetail}
                onChange={e => setReasonDetail(e.target.value)}
                placeholder="e.g. Acme Corp, Buildrite"
                className="w-full px-3.5 py-3 rounded-xl border text-sm outline-none transition-colors"
                style={{
                  backgroundColor: theme.background,
                  borderColor:     theme.border,
                  color:           theme.text,
                }}
              />
            </>
          )}

          {/* Vehicle chips */}
          {vehicleList.length > 0 && (
            <>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-2 mt-3" style={{ color: theme.textMuted }}>
                Vehicle
              </p>
              <div className="flex gap-2 overflow-x-auto pb-2 mb-1 scrollbar-hide">
                {vehicleList.map(v => {
                  const active = vehicleId === v.id;
                  const label  = v.nickname || v.registration;
                  return (
                    <button key={v.id} onClick={() => setVehicleId(active ? null : v.id)}
                      className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-xs font-semibold transition-all active:scale-95"
                      style={{
                        borderColor:     active ? PRIMARY : theme.border,
                        backgroundColor: active ? `${PRIMARY}18` : 'transparent',
                        color:           active ? PRIMARY : theme.textMuted,
                      }}>
                      <Car size={11} />
                      {label}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Notes */}
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2 mt-3" style={{ color: theme.textMuted }}>
            Notes
          </p>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Add notes about this trip..."
            rows={3}
            className="w-full px-3.5 py-3 rounded-xl border text-sm resize-none outline-none transition-colors"
            style={{
              backgroundColor: theme.background,
              borderColor:     theme.border,
              color:           theme.text,
            }}
          />
        </Card>

        {/* Save button */}
        {error && (
          <div className="px-4 py-3 rounded-xl text-sm"
            style={{ backgroundColor: theme.isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2', color: '#f87171' }}>
            {error}
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 rounded-2xl text-white font-bold text-base transition-all disabled:opacity-60 active:scale-[0.98]"
          style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}
        >
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>

      </div>

      <style>{`
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}